import {
  createAsyncThunk,
  createSlice,
  current,
  PayloadAction,
} from '@reduxjs/toolkit';

import { Section } from '../../config/section';
import {
  ICTask,
  ISUpdateCoreTask,
  MergefloDealspaceUsers,
  TaskResponse,
} from '../../helper/Interfaces';
import { RootState } from '../store';
import { DataManagement } from '@Helper/DataManagement';
import axios from 'axios';

const BaseURL = process.env.REACT_APP_MERGEFLO_API_URL ?? '';
const Version = process.env.REACT_APP_MERGEFLO_API_VERSION ?? '';

export interface IProps {
  playBookId: string;
  sectionList: Section[] | undefined;
  dealTaskList: ICTask[];
  userList: MergefloDealspaceUsers[];
  workSpaceId: string;
  newTaskVisibleID: string;
  hasChanges: number;
  userLoading: boolean;
}

export interface PatchTaskProps {
  id: string;
  type: string;
  value: any;
}

const initialState: IProps = {
  playBookId: '',
  sectionList: undefined,
  dealTaskList: [],
  userList: [],
  workSpaceId: '',
  newTaskVisibleID: '',
  hasChanges: 0,
  userLoading: false,
};

export const getUsersList = createAsyncThunk(
  'dealspace/getUsersList',
  async (initialID: string, { getState }) => {
    const dataManagement = new DataManagement();
    const response = await axios.get(
      BaseURL + Version + 'mergeflo/dealspace/' + initialID + '/users',
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + dataManagement.getData('userToken'),
        },
      }
    );

    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  }
);

export const patchDealTaskList = createAsyncThunk(
  'dealspace/patchDealTaskList',
  async (initial: PatchTaskProps) => {
    const dataManagement = new DataManagement();

    const updateTemplate = {};
    (updateTemplate as any)[initial.type] = initial.value;

    const response = await axios.patch(
      BaseURL + Version + 'mergeflo/task/core/' + initial.id,
      updateTemplate,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + dataManagement.getData('userToken'),
        },
      }
    );

    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  }
);

export const playBookDetailSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    postPlayBookId: (state, action: PayloadAction<string>) => {
      state.playBookId = action.payload;
    },
    postWorkSpaceId: (state, action: PayloadAction<string>) => {
      state.workSpaceId = action.payload;
    },
    postSectionList: (state, action: PayloadAction<Section[] | undefined>) => {
      state.sectionList = action.payload;
    },

    postDealTaskList: (state, action: PayloadAction<ICTask[]>) => {
      state.dealTaskList = action.payload;
    },
    postDealNewTaskToList: (state, action: PayloadAction<TaskResponse>) => {
      console.log('action', action);
      if (action.payload) {
        const data = current(state.dealTaskList);
        const row = data.find(f => f.id === '-1');

        if (row) {
          const index = data.indexOf(row);
          let dataList = [
            ...data.slice(0, index),
            action.payload.taskDto[0],
            ...data.slice(index + 1),
          ];

          if (action.payload.sectionOrders) {
            dataList = dataList.map(el => {
              var found = action.payload.sectionOrders?.find(
                s => s.taskId === el.id
              );
              if (found) {
                el = Object.assign({
                  ...el,
                  orderNumber: found.sectionOrder,
                });
              }
              return el;
            });
          }

          if (action.payload.familyOrders) {
            dataList = dataList.map(el => {
              var found = action.payload.familyOrders?.find(
                s => s.taskId === el.id
              );
              if (found) {
                el = Object.assign({
                  ...el,
                  orderNumber: found.familyOrder,
                });
              }
              return el;
            });
          }

          if (action.payload.taskDto[0].parentId) {
            const task = action.payload.taskDto[0];
            const item = dataList.find(i => i.id === task.parentId);
            if (item) {
              let dealList = JSON.parse(JSON.stringify(dataList)) as ICTask[];
              const foundItem = dealList.find(
                i => i.id === task.parentId
              ) as ICTask;
              dealList = dealList.filter(i => i.id !== item.id);
              dealList = dealList.filter(i => i.id !== '-1');
              switch (task.type ?? '') {
                case 'risk':
                  foundItem.riskCount = (foundItem.riskCount ?? 0) + 1;
                  break;
                case 'decision':
                  foundItem.decisionCount = (foundItem.decisionCount ?? 0) + 1;
                  break;
                case 'issue':
                  foundItem.issueCount = (foundItem.issueCount ?? 0) + 1;
                  break;
                default:
                  foundItem.subTaskCount = (foundItem.subTaskCount ?? 0) + 1;
                  break;
              }
              state.dealTaskList = [...dealList, foundItem];
            }
          } else {
            state.dealTaskList = dataList;
          }
        }
      }
      // state.dealTaskList = [...action.payload, ...state.dealTaskList];
    },
    postUserList: (state, action: PayloadAction<MergefloDealspaceUsers[]>) => {
      state.userList = action.payload;
    },
    postNewTaskVisibleID: (state, action: PayloadAction<string>) => {
      state.newTaskVisibleID = action.payload;
    },
    postListChanges: (state, action: PayloadAction<number>) => {
      state.hasChanges = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getUsersList.pending, state => {
        state.userLoading = true;
      })
      .addCase(getUsersList.fulfilled, (state, action) => {
        if (action.payload) {
          state.userList = action.payload;
        }
        state.userLoading = false;
      })

      .addCase(patchDealTaskList.fulfilled, (state, action) => {
        if (action.payload) {
          const data = current(state.dealTaskList);
          const row = data.find(f => f.id === action.payload[0].id);

          if (row) {
            const index = data.indexOf(row);
            const dataList = [
              ...data.slice(0, index),
              action.payload[0],
              ...data.slice(index + 1),
            ];
            state.dealTaskList = dataList;
          }
        }
      });
  },
});

export const {
  postSectionList,
  postPlayBookId,
  postUserList,
  postWorkSpaceId,
  postDealTaskList,
  postDealNewTaskToList,
  postNewTaskVisibleID,
  postListChanges,
} = playBookDetailSlice.actions;

export const getPlayBookDetail = (state: RootState) =>
  state.playBookDetailReducer;

export default playBookDetailSlice.reducer;
